import { CommonTooltips, CommonDialogs } from "@mcleod/common";
import { Table, DataSourceExecutionEvent, DataSourceMode, ClickEvent, TableRow, LookupModelSelectionEvent, TableRowExpansionEvent, TableRowDisplayEvent, Label, Textbox, SelectionType, ContextMenuItemCreator, Tab, Panel, Component, TableSelectionEvent, Layout, DecoratorFactory, SlideoutDecorator, AddType, Snackbar, Button, SelectionEvent } from "@mcleod/components";
import { ActionPermission, Alignment, Api, DateUtil, DisplayType, LogManager, ModelRow, Navigation, PermissionsUtil, RowUpdateEvent, StringUtil, UrlUtil } from "@mcleod/core";
import { SecondaryFilterProps, FilterPanel } from "@mcleod/dispatch";
import { OrderList } from "@mcleod/dispatch/src/OrderList";
import { TableComment, LMEUserSettings } from "@mcleod/general";
import { QuoteList } from "@mcleod/powerbroker/src/QuoteList";
import { AutogenLayoutCustomerRelationshipManager } from "./autogen/AutogenLayoutCustomerRelationshipManager";
import { CRMMultiSelectErrorModal } from "./CRMMultiSelectErrorModal";
import { RowCallListProfile, ModelCallListProfile } from "./models/ModelCallListProfile";
import { ModelProspect, RowProspect } from "./models/ModelProspect";
import { ProspectAction } from "./ProspectAction";
import { SalesExpand } from "./SalesExpand";
import { ModelProspectAction } from "./models/ModelProspectAction";
import { SendInfoPacket } from "./SendInfoPacket";


const log = LogManager.getLogger("lme.crm.CustomerRelationshipManager");

const DUE_TODAY: SecondaryFilterProps = {
    id: "dueToday",
    caption: "Due Today",
    filter: (row: ModelRow) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dueDate = row.getDate("date_due");
        return dueDate && dueDate >= today && dueDate < tomorrow;
    }
}

const PAST_DUE: SecondaryFilterProps = {
    id: "pastDue",
    caption: "Past Due",
    filter: (row: ModelRow) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = row.getDate("date_due");
        return dueDate && dueDate < today;
    }
}

const LOW_PRIORITY: SecondaryFilterProps = {
    id: "lowPriority",
    caption: "Low Priority",
    filter: (row: ModelRow) => {
        return row.get("priority") == "2";
    }
}

const MEDIUM_PRIORITY: SecondaryFilterProps = {
    id: "mediumPriority",
    caption: "Medium Priority",
    filter: (row: ModelRow) => {
        return row.get("priority") == "1";
    }
}

const HIGH_PRIORITY: SecondaryFilterProps = {
    id: "highPriority",
    caption: "High Priority",
    filter: (row: ModelRow) => {
        return row.get("priority") == "0";
    }
}

const NO_PRIORITY: SecondaryFilterProps = {
    id: "noPriority",
    caption: "No Priority",
    filter: (row: ModelRow) => {
        return row.get("priority") == "3";
    }
}

enum FilterType {
    CUSTOMER, PROSPECT, OPPORTUNITY
}

enum InitialFilter {
    PAST_DUE = "P",
    DUE_TODAY = "T"
}

interface CallListFilter {
    type: FilterType;
    table: Table;
    id: string;
}

export class CustomerRelationshipManager extends AutogenLayoutCustomerRelationshipManager {
    private _callListProfile: string;
    private _profileRow: RowCallListProfile;
    private filterBeforeSearch: CallListFilter;
    private _currentFilter: CallListFilter;
    private salesData: ModelRow[] = [];
    private initialFilterLoaded = false;
    private filterPanel: FilterPanel;
    private noDataProspects = [];
    private noDataCustomers = [];
    private noDataOpportunities = [];

    async onLoad(): Promise<void> {
        this.splitterCallList.buttonExpandFirst.clicked();
        this.filterPanel = new FilterPanel([DUE_TODAY, PAST_DUE, LOW_PRIORITY, MEDIUM_PRIORITY, HIGH_PRIORITY, NO_PRIORITY], this.tableActivities, this.panelSecondaryFilters);
        this.updateButtonStates(null);
        this.mainDataSource.addBusyComponent(this.tableProspects);
        this.mainDataSource.addBusyComponent(this.tableCustomers);
        this.mainDataSource.addBusyComponent(this.tableOpportunities);
    }

    sourceProspectiveSalesAfterExecution(event: DataSourceExecutionEvent) {
        this.salesData = event.dataSource.data;
        if (this.sourceProspectiveSales.summaryData) {
            const promises = [];
            const prospects = this.sourceProspectiveSales.summaryData.get("prospects", []);
            const includedProspects = this.sourceProspectiveSales.summaryData.get("incl_prospects", []);
            this.noDataProspects = includedProspects;
            const allProspects = prospects.concat(includedProspects);
            if (allProspects?.length > 0) {
                promises.push(this.sourceProspect.search({ id: `in ${allProspects.join(',')}` }));
            } else if (this.sourceProspect.rowCount > 0) {
                this.sourceProspect.setRowsAndMode(DataSourceMode.NONE, []);
            }

            const customers = this.sourceProspectiveSales.summaryData.get("customers", []);
            const includedCustomers = this.sourceProspectiveSales.summaryData.get("incl_customers", []);
            this.noDataCustomers = includedCustomers;
            const allCustomers = customers.concat(includedCustomers);
            if (allCustomers?.length > 0) {
                promises.push(this.sourceCustomer.search({ id: `in ${allCustomers.join(',')}` }));
            } else if (this.sourceCustomer.rowCount > 0) {
                this.sourceCustomer.setRowsAndMode(DataSourceMode.NONE, []);
            }

            const opportunities = this.sourceProspectiveSales.summaryData.get("opportunities", []);
            const includedOpportunities = this.sourceProspectiveSales.summaryData.get("incl_opportunities", []);
            this.noDataOpportunities = includedOpportunities;
            const allOpportunities = opportunities.concat(includedOpportunities);
            if (allOpportunities?.length > 0) {
                promises.push(this.sourceOpportunity.search({ id: `in ${allOpportunities.join(',')}` }));
            } else if (this.sourceOpportunity.rowCount > 0) {
                this.sourceOpportunity.setRowsAndMode(DataSourceMode.NONE, []);
            }
            Promise.all(promises).then(() => {
                if (this.filterBeforeSearch) {
                    this.currentFilter = this.filterBeforeSearch;
                    this.filterBeforeSearch = null;
                    this.reapplyFilter(this.currentFilter)
                }
                if (!this.initialFilterLoaded) {
                    this.initialFilterLoaded = true;
                    const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
                    if (urlParams?.filter) {
                        if (urlParams.filter === InitialFilter.PAST_DUE) {
                            this.filterPanel.toggleFilterById(PAST_DUE.id);
                        } else if (urlParams.filter === InitialFilter.DUE_TODAY) {
                            this.filterPanel.toggleFilterById(DUE_TODAY.id);
                        }
                    }
                }
            });
        }
        this.textboxLastRefresh.text = DateUtil.formatDateTime(new Date());
    }

    buttonRefreshOnClick(event: ClickEvent) {
        this.searchCallListFiltered();
    }

    reapplyFilter(filter: CallListFilter) {
        if (filter) {
            const tableRow = filter.table.allRows.find(row => row.data.getString("id") === filter.id);
            if (tableRow) {
                filter.table.selectedRow = tableRow;
            }
        }
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        if (modelRow.isNull("customer_id") && !modelRow.isNull("prospect_id")) {
            modelRow.set("customer_id", modelRow.get("prospect_id"));
        }
        if (modelRow.isNull("prospect_action_id")) {
            return this.showAddRecurringCallActivitySlideout(modelRow);
        } else {
            return ProspectAction.showEditActionSlideout(modelRow, {
                onSave: (updatedRow) => updatedRow.post().then(() => this.searchCallListFiltered()),
                onDelete: () => this.searchCallListFiltered()
            });
        }
    }

    showAddRecurringCallActivitySlideout(modelRow: ModelRow<any>) {
        ProspectAction.showAddActionSlideout({
            "action_type": modelRow.getString("action_type"),
            "activity_id": modelRow.get("activity_id"),
            "customer_id": modelRow.getString("prospect_id"),
            "primary_contact": modelRow.getString("contact"),
            "salesperson_id": modelRow.getString("salesperson"),
            "operations_user": modelRow.getString("operations_user"),
            "date_due": modelRow.getDate("date_due"),
            "action_date": new Date(),
            "status": "C",
            "descr": modelRow.getString("activity"),
            "phone": modelRow.getString("contact_phone"),
            "email": modelRow.getString("contact_email")
        }, {
            doAfterSlideIn: (slideout) => {
                const layout = slideout.layout as ProspectAction;
                layout.textboxCustomerId.enabled = false;
                layout.activeRow.setLookupModelData("customer_id", { name: modelRow.getString("prospect_name") }); layout.activeRow.setLookupModelData("activity_id", { "id": modelRow.getString("activity_id") });
                layout.activeRow.setLookupModelData("primary_contact", { "name": modelRow.getString("contact") });
                layout.activeRow.setLookupModelData("salesperson_id", { "name": modelRow.getString("salesperson_name") });
                layout.activeRow.setLookupModelData("operations_user", { "name": modelRow.getString("operations_user_name") });
                const opportunityId = modelRow.getNumber("opportunity_id", null);
                if (opportunityId) {
                    layout.activeRow.setValueAndLookupModelData("opportunity_id", opportunityId, {
                        id: opportunityId.toString(),
                    });
                }
                layout.mainDataSource.displayDataInBoundComponents();
                layout.initializeContactFilter(modelRow.getString("prospect_id"));
            },
            onSave: (updatedRow) => updatedRow.post().then(() => this.searchCallListFiltered())
        });
    }

    tableActivitiesOnRowExpand(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const expandLayout = event.expandComponents[0] as SalesExpand;
        expandLayout.search({"id" : modelRow.get("prospect_action_id")})
        .then(() => {
            expandLayout.activeRow.set("status", "C");
            expandLayout.activeRow.set("action_date", new Date());
            expandLayout.mainDataSource.activeRow.addAfterPostListener(async (event: RowUpdateEvent) =>
                this.searchCallListFiltered()
            )
        });
    }

    searchCallListFiltered() {
        this.filterBeforeSearch = this.currentFilter;
        this.mainDataSource.search({ profile_row: this.profileRow });
    }

    tableActivitiesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const labelDateDue = tableRow.findComponentById("labelDateDue") as Label;
        const labelPriority = tableRow.findComponentById("labelPriority") as Label;
        if (labelDateDue) {
            const dueDate = modelRow.getDate(labelDateDue.field);
            if (dueDate) {
                const now = new Date().setHours(0,0,0,0);
                const due = dueDate.setHours(0,0,0,0);
                if (due < now) {
                    labelDateDue.color = "error";
                    labelDateDue.fontBold = true;
                } else if (due === now) {
                    labelDateDue.color = "warning";
                    labelDateDue.fontBold = true;
                }
            }
        }
        if (labelPriority) {
            const priority = modelRow.getString(labelPriority.field);
            if (priority == 1) {
                labelPriority.backgroundColor = "warning";
            } else if (priority == 0) {
                labelPriority.backgroundColor = "error";
            }
            else if (priority == 3) {
                labelPriority.visible = false;
            }
        }
        const textboxMarketingPlan = tableRow.findComponentById("textboxMarketingPlan") as Textbox;
        if (textboxMarketingPlan) {
            const planRow = new ModelRow("lme/crm/market-plan", false, {
                id: modelRow.getString("marketing_plan"),
                descr: modelRow.getString("marketing_plan_descr")
            });
            textboxMarketingPlan.quickInfoOverrideSearchMethod = (layout, keyValue) => {
                layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [planRow]);
                return Promise.resolve();
            }
        }
        const labelProspectName = tableRow.findComponentById("labelProspectName") as Label;
        if (labelProspectName) {
            CommonTooltips.setTooltipContentLayout(labelProspectName, modelRow.getString("prospect_id"), "lme/crm/ProspectQuickInfo");
        }

        const imageOpportunity = tableRow.findComponentById("imageOpportunity");
        if (imageOpportunity) {
            CommonTooltips.setTooltipContentLayout(imageOpportunity, modelRow.get("opportunity_id", null), "lme/crm/OpportunityQuickInfo");
        }

        tableRow.expandable = !tableRow.data.isNull("prospect_action_id");
        tableRow.contextMenuItems = () => {
            if (tableRow.table.selectedRows == null || !tableRow.table.selectedRows.includes(tableRow)) {
                tableRow.table.selectRow(tableRow, SelectionType.SINGLE, null);
            }
            const buttons: any[] = []
            if (!(tableRow.table.selectedRows.length > 1
            && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTISELECT_CRM_COMPLETE))) {
                buttons.push({
                    visible: true, text: "Complete Action", button: this.createCompleteActionMenuItem(tableRow.table)
                });
            }
            buttons.push(
                { button: this.buttonReschedule, text: "Reschedule Activity" },
            );
            return ContextMenuItemCreator.fromButtons(...buttons);
        }
    }

    tableProspectsOnRowDisplay(event: TableRowDisplayEvent){
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const labelName = tableRow.findComponentById("labelName") as Label;
        if (labelName) {
            CommonTooltips.setTooltipContentLayout(labelName, modelRow.getString("id"), "lme/crm/ProspectQuickInfo");
             if (this.noDataProspects.includes(modelRow.getString("id"))) {
                labelName.color = "subtle";
                labelName.fontBold = true;
            }
        }
        const labelContactname = tableRow.findComponentById("labelContactname") as Label;
        if (labelContactname) {
            this.buildContactTooltip(labelContactname, modelRow.getString("contact.name"), modelRow.getString("contact.phone"), modelRow.getString("contact.email"));
        }
    }

    buttonAddActionOnClick(event: ClickEvent) {
        let filterType: FilterType;
        let table: Table;
        ProspectAction.showAddActionSlideout(null, {
            doAfterSlideIn: (slideout) => {
                const activeTab = this.tabsetCRM.getActiveTab() as Tab;
                const layout = slideout.layout as ProspectAction;
                if (activeTab.id === "tabProspect" && this.tableProspects.selectedRow) {
                    filterType = FilterType.PROSPECT;
                    const selectedRow = this.tableProspects.selectedRow.data;
                    const prospectId = selectedRow.getString("id");
                    const prospectName = selectedRow.getString("name");
                    table = this.tableProspects;
                    layout.textboxCustomerId.enabled = false;
                    layout.activeRow.setValueAndLookupModelData("customer_id", prospectId, {name: prospectName});
                    layout.textboxCustomerIdOnLookupModelSelection(new LookupModelSelectionEvent(layout.textboxCustomerId, selectedRow));
                } else if (activeTab.id === "tabCustomer" && this.tableCustomers.selectedRow) {
                    filterType = FilterType.CUSTOMER;
                    const selectedRow = this.tableCustomers.selectedRow.data;
                    const customerId = selectedRow.getString("id");
                    const customerName = selectedRow.getString("name");
                    table = this.tableCustomers;
                    layout.textboxCustomerId.enabled = false;
                    layout.activeRow.setValueAndLookupModelData("customer_id", customerId, {name: customerName});
                    layout.textboxCustomerIdOnLookupModelSelection(new LookupModelSelectionEvent(layout.textboxCustomerId, selectedRow));
                } else if (activeTab.id === "tabOpportunity" && this.tableOpportunities.selectedRow) {
                    filterType = FilterType.OPPORTUNITY;
                    const selectedRow = this.tableOpportunities.selectedRow.data;
                    const opportunityId = selectedRow.getNumber("id");
                    const customerId = selectedRow.getString("customer_id");
                    const customerName = selectedRow.getString("customer.name");
                    const salespersonId = selectedRow.getString("customer.salesperson_id");
                    table = this.tableOpportunities;
                    layout.textboxCustomerId.enabled = false;
                    if (customerId && customerName) {
                        layout.activeRow.setValueAndLookupModelData("customer_id", customerId, {name: customerName});
                        const customerRow = new ModelRow("lme/ar/customer", false, {id: customerId, name: customerName, salesperson_id: salespersonId});
                        customerRow.setValueAndLookupModelData("salesperson_id", salespersonId, {id: salespersonId, name: selectedRow.getString("salesperson.name")});
                        layout.textboxCustomerIdOnLookupModelSelection(new LookupModelSelectionEvent(layout.textboxCustomerId, customerRow));
                    }

                    if (opportunityId) {
                        layout.activeRow.setValueAndLookupModelData("opportunity_id", opportunityId.toString(), {
                            id: opportunityId.toString(),
                        });
                        layout.textboxOpportunityId.displayComponentData(layout.activeRow, null, 0);
                    }
                }
                else {
                    layout.textboxCustomerId.enabled = true;
                }
            },
            onSave: async (updatedData) => {
                this.filterBeforeSearch = this.currentFilter;
                updatedData.post().then(() => this.sourceProspectiveSales.search(this.sourceProspectiveSales.lastSearch));
            }
        });
    }

    tableCustomersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const labelName = tableRow.findComponentById("labelName") as Label;
        if (labelName) {
            CommonTooltips.setTooltipContentLayout(labelName, modelRow.getString("id"), "lme/crm/ProspectQuickInfo");
            if (this.noDataCustomers.includes(modelRow.getString("id"))) {
                labelName.color = "subtle";
                labelName.fontBold = true;
            }
        }
        const labelContactname = tableRow.findComponentById("labelContactname") as Label;
        if (labelContactname) {
            this.buildContactTooltip(labelContactname, modelRow.getString("contact.name"), modelRow.getString("contact.phone"), modelRow.getString("contact.email"));
        }
    }

    tableOpportunitiesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const panelIdentifiers = tableRow.findComponentById("panelIdentifiers") as Panel;
        if (panelIdentifiers) {
            CommonTooltips.setTooltipContentLayout(panelIdentifiers, modelRow.getNumber("id").toString(), "lme/crm/OpportunityQuickInfo");
            if (this.noDataOpportunities.includes(modelRow.getNumber("id").toString())) {
                panelIdentifiers.color = "subtle";
                panelIdentifiers.fontBold = true;
            }
        }
        const labelCustomername = tableRow.findComponentById("labelCustomername") as Label;
        if (labelCustomername) {
            CommonTooltips.setTooltipContentLayout(labelCustomername, modelRow.getString("customer_id"), "lme/crm/ProspectQuickInfo");
        }
        const labelPrimaryContact = tableRow.findComponentById("labelPrimaryContact") as Label;
        if (labelPrimaryContact) {
            if (!modelRow.isNull("contact_id")) {
                this.buildContactTooltip(labelPrimaryContact,
                    modelRow.getString("contact.name", null),
                    modelRow.getString("contact.phone", null),
                    modelRow.getString("contact.email", null)
                );
            } else {
                CommonTooltips.setTooltipContentTextboxes(labelPrimaryContact, [{ captionVisible: false, text: "No contact information available." }]);
            }
        }
    }

    buildContactTooltip(component: Component, contactName: string, phone: string, email: string) {
        CommonTooltips.setTooltipContentTextboxes(component, [
            { caption: "Contact", text: contactName ?? "--"},
            { caption: "Phone", text: phone ?? "--", displayType: DisplayType.PHONE },
            { caption: "Email", text: email ?? "--", displayType: DisplayType.EMAIL }
        ]);
    }

    tableActivitiesOnSelect(event: TableSelectionEvent) {
        const selectedRows = event.table.selectedRows;
        const opportunityId = event.table.selectedRow?.data.get("opportunity_id", null);
        const typeOf = event.table.selectedRow?.data.getString("customer_type_of", "P");
        const filterType = opportunityId ? FilterType.OPPORTUNITY : (typeOf === "C" ? FilterType.CUSTOMER : FilterType.PROSPECT);
        const id = filterType === FilterType.OPPORTUNITY
            ? opportunityId
            : event.table.selectedRow?.data.getString("prospect_id");
        this.setSummaryPanel(filterType, id);
        this.updateButtonStates(selectedRows);
    }

    tableCustomersOnSelect(event: TableSelectionEvent) {
        this.buildFilter(FilterType.CUSTOMER, event);
    }

    tableProspectsOnSelect(event: TableSelectionEvent) {
        this.buildFilter(FilterType.PROSPECT, event);
    }

    tableOpportunitiesOnSelect(event: TableSelectionEvent) {
        this.buildFilter(FilterType.OPPORTUNITY, event);
    }

    buildFilter(type: FilterType, event: TableSelectionEvent) {
        if (event.table.selectedRow) {
            this.currentFilter = {
                type: type,
                table: event.table,
                id: type == FilterType.OPPORTUNITY
                    ? event.table.selectedRow.data.getNumber("id").toString()
                    : event.table.selectedRow.data.getString("id")
            };
        } else {
            this.currentFilter = null;
        }
    }

    private updateButtonStates(selectedRows: TableRow[]) {
        const hasSelection = selectedRows != null && selectedRows.length > 0;
        const isMultiSelect = hasSelection && selectedRows.length > 1;
        const isCustomer = hasSelection && selectedRows[0].data.getString("customer_type_of", "P") === "C";

        this.buttonReschedule.enabled = hasSelection
            && !(isMultiSelect && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTISELECT_CRM_RESCHEDULE));

        this.buttonAddQuote.enabled = hasSelection && !isMultiSelect;
        this.buttonQuoteHistory.enabled = hasSelection && !isMultiSelect;
        this.buttonSearchOrders.enabled = !isMultiSelect && isCustomer;
        this.buttonViewComments.enabled = hasSelection && !isMultiSelect;
    }

    buttonAddQuoteOnClick(event: ClickEvent) {
        const selection = this.tableActivities.selectedRow.data;

        const customerId = selection.getString("prospect_id");
        if (!customerId) return;
        const contact = selection.getString("contact");
        const phone = selection.getString("contact_phone");
        const email = selection.getString("contact_email");
        const params = {
            mode: "add", customerId, contact, phone, email
        };

        Navigation.navigateTo("lme/powerbroker/QuickQuote" + UrlUtil.buildQueryString(params), { newTab: true });
    }

    buttonQuoteHistoryOnClick(event: ClickEvent) {
        const selection = this.tableActivities.selectedRow.data;
        const customerId = selection.getString("prospect_id");
        if (!customerId) return;
        const customerName = selection.getString("prospect_name", "").replace(/'/g, "''");

        // Navigate to quote history for the selected customer
        // This would typically open a quote list filtered by customer
        const layout = Layout.getLayout("lme/powerbroker/QuoteList", { displayingFromToolbox: true }) as QuoteList;
        layout.addLayoutLoadListener(event => {
            const decorated = DecoratorFactory.decoratePage(layout, null) as Layout;
            new SlideoutDecorator({
                layout: decorated,
                title: "Quote History",
                transitionOptions: { direction: Alignment.BOTTOM },
                displayOverlay: true,
                overlayProps: { closeOnClickOff: false, coverPageHeader: false },
                fillHorizontalSpace: true,
                fillVerticalSpace: true
            });
            const lmData = {
                id: customerId,
                name: customerName,
            };
            layout.sourceQuoteOrder.searchRow.setValueAndLookupModelData("customer_name", customerId, lmData);
            layout.panelSearchTop.displayData(layout.sourceQuoteOrder.searchRow, null, 0);
            layout.sourceQuoteOrder.executeValidatedSearch();
        });
    }

    buttonSearchOrdersOnClick(event: ClickEvent) {
        const selection = this.tableActivities.selectedRow.data;
        const customerId = selection.getString("prospect_id");
        if (!customerId) return;
        const customerName = selection.getString("prospect_name");

        const layout = Layout.getLayout("lme/dispatch/OrderList", { displayingFromToolbox: true }) as OrderList;
        layout.addLayoutLoadListener(event => {
            const decorated = DecoratorFactory.decoratePage(layout, null) as Layout;
            new SlideoutDecorator({
                layout: decorated,
                title: "Orders",
                transitionOptions: { direction: Alignment.BOTTOM },
                displayOverlay: true,
                overlayProps: { closeOnClickOff: false, coverPageHeader: false },
                fillHorizontalSpace: true,
                fillVerticalSpace: true
            });
            const lmData = {
                id: customerId,
                name: customerName,
            };
            layout.sourceOrdersList.searchRow.setValueAndLookupModelData("customer_id", customerId, lmData);
            const today = new Date();
            const threeSixty = new Date();
            threeSixty.setDate(today.getDate() - 360);
            const dateRange = DateUtil.formatDate(threeSixty, "MM/dd/yyyy") + " - " + DateUtil.formatDate(today, "MM/dd/yyyy");
            layout.sourceOrdersList.searchRow.set("ordered_date", dateRange);
            layout.panelOrderSearch.displayData(layout.sourceOrdersList.searchRow, null, 0);
            layout.sourceOrdersList.executeValidatedSearch();
        });
    }

    buttonCommentsOnClick(event: ClickEvent) {
        let entityId: string;
        let entityType: string;
        let entityName: string;
        let parentRowType: string;

        const selection = this.tableActivities.selectedRow.data;
        if (selection.isNull("opportunity_id")) {
            entityId = selection.getString("prospect_id");
            entityType = selection.getString("customer_type_of", "P") === "C" ? "customer" : "prospect";
            entityName = selection.getString("prospect_name");
            parentRowType = 'C';
        } else {
            entityId = selection.getString("opportunity_id");
            entityType = "opportunity";
            entityName = selection.getString("opportunity_name") || `Opportunity #${entityId}`;
            parentRowType = 'I';
        }

        if (!entityId) return;

        // // Open comments slideout - this would need to be implemented based on your comments system
        const layout = Layout.getLayout("lme/general/TableComment", { displayingFromToolbox: true }) as TableComment;
        layout.addLayoutLoadListener(event => {
            const decorated = DecoratorFactory.decoratePage(layout, null) as Layout;
            new SlideoutDecorator({
                layout: decorated,
                title: `Comments - ${entityName}`,
                transitionOptions: { direction: Alignment.BOTTOM },
                displayOverlay: true,
                overlayProps: { closeOnClickOff: false, coverPageHeader: false },
                fillHorizontalSpace: true,
                fillVerticalSpace: true
            });
            // Filter comments by entity type and ID
            layout.mainDataSource.search({
                parent_row_id: entityId,
                parent_row_type: parentRowType
            });
            layout.tableComments.addType = AddType.NOT_ALLOWED;
            layout.tableComments.displayToolsCell = false;
        });
    }

    // called from MultiProfilePlanningTabs.ts
    async loadProfile(profile?: string | ModelRow) {
        log.debug("Loading profile")
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? this.callListProfile ?? LMEUserSettings.get()?.call_list_profile;
        if (profileId == null)
            Snackbar.showSnackbar("You do not have a default call list profile defined. "
                + "Add a default profile to your preferences so your orders will automatically "
                + "display or you can select a profile above to get started.", { persist: true });
        else {
            const profileRow = await new ModelCallListProfile().searchSingle({ id: profileId });
            if (profileRow == null)
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            else
                this.setProfileRow(profileRow);
        }
    }

    public setProfileRow(value: ModelRow) {
        if (value != this._profileRow) {
            this._profileRow = value;
            if (this._profileRow != null) {
                this.searchMainDatasource();
            }
        }
    }

    searchMainDatasource() {
        this.mainDataSource.search({
            profile_row: this.profileRow
        });
    }

    override get windowTitle(): string {
        const title = "Customer Relationship Manager";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }

    public get callListProfile(): string {
        return this._callListProfile;
    }

    public set callListProfile(value: string) {
        this._callListProfile = value;
    }

    public get profileRow(): RowCallListProfile {
        return this._profileRow;
    }

    public set profileRow(value: RowCallListProfile) {
        this._profileRow = value;
    }

    public get currentFilter(): CallListFilter {
        return this._currentFilter;
    }

    public set currentFilter(value: CallListFilter) {
        this._currentFilter = value;
        if (value) {
            this.filterCallList(value.type, value.id);
        } else {
            this.resetCallList();
        }
    }

    filterCallList(filterType: FilterType, id: string) {
        this.sourceProspectiveSales.data = this.salesData.filter(row => {
            switch (filterType) {
                case FilterType.CUSTOMER:
                case FilterType.PROSPECT:
                    return row.getString("prospect_id") === id;
                case FilterType.OPPORTUNITY:
                    return row.getString("opportunity_id") === id;
            }
        });
        this.sourceProspectiveSales.displayDataInBoundComponents();
        this.setSummaryPanel(filterType, id);
    }

    setSummaryPanel(filterType: FilterType, id: string) {
        if (filterType == null || StringUtil.isEmptyString(id)) {
            this.layoutCustomerSummary.visible =
                this.layoutProspectSummary.visible =
                this.layoutOpportunitySummary.visible = false;
            this.splitterCallList.buttonExpandFirst.clicked();
            return;
        }
        switch (filterType) {
            case FilterType.CUSTOMER:
                this.layoutCustomerSummary.customerId = id;
                this.layoutCustomerSummary.visible = true;
                this.layoutProspectSummary.visible = false;
                this.layoutOpportunitySummary.visible = false;
                break;
            case FilterType.PROSPECT:
                this.layoutProspectSummary.prospectId = id;
                this.layoutCustomerSummary.visible = false;
                this.layoutProspectSummary.visible = true;
                this.layoutOpportunitySummary.visible = false;
                break;
            case FilterType.OPPORTUNITY:
                this.layoutOpportunitySummary.opportunityId = id;
                this.layoutCustomerSummary.visible = false;
                this.layoutProspectSummary.visible = false;
                this.layoutOpportunitySummary.visible = true;
                break;
        }
        if (!!this.splitterCallList["_expandedFirstComponent"]) {
            this.splitterCallList.buttonExpandSecond.clicked();
            this.splitterCallList.position = "80%";
        }
    }

    resetCallList() {
        this.sourceProspectiveSales.data = this.salesData;
        this.sourceProspectiveSales.displayDataInBoundComponents();
        this.setSummaryPanel(null, null);
    }

    createCompleteActionMenuItem(table: Table) {
        return new Button({
            caption: "Complete Activities",
            onClick: () => this.completeActions(table)
        });
    }

    completeActions(table: Table) {
        const actions = table.selectedRows.map(row => row.data);
        CommonDialogs.showInputDialog("Enter the remarks for completed activities", "Complete Activities", {
            required: true
        }, {
            panelTitleProps: { backgroundColor: "primary.darker", color: "primary.reverse" },
            okProps: { backgroundColor: "primary", color: "primary.reverse", width: 110 },
        }).then((response) => {
            if (!StringUtil.isEmptyString(response)) {
                if (actions.length === 1) {
                    return this.completeAction(actions[0], response);
                }
                const progressSnackbar = Snackbar.showDownloadSnackbar("Action in Progress", "Marking complete...", { persist: true });
                return Api.post("lme/crm/complete-prospect-action", { actions, remarks: response })
                .then((result: any) => {
                    if (actions.length > 1 || result.data?.filter(r => !!r.error).length > 0) {
                        CRMMultiSelectErrorModal.showModal(
                            "Complete Activities",
                            result.data?.filter(r => !r.error).length,
                            result.data?.filter(r => !!r.error).map(r => new ModelRow(null, false, r))
                        );
                    }
                })
                .finally(() => {
                    progressSnackbar.dismiss();
                    this.searchCallListFiltered();
                })
            }
        });
    }

    completeAction(action: ModelRow, response: string): any {
        const id = action.getString("prospect_action_id");
        if (!id) {
            return Api.post("lme/crm/complete-prospect-action", { actions: [action], remarks: response })
                .finally(() => this.searchCallListFiltered());
        } else {
            return new ModelProspectAction().searchSingle({ id }, "lme/crm/ProspectAction")
                .then((actionRow) => {
                    actionRow.set("status", "C");
                    actionRow.set("action_date", new Date());
                    actionRow.set("remarks", response);
                    return actionRow.post();
                }).then(updatedRow => {
                    if (!updatedRow.isNull("info_packet_id")) {
                        return SendInfoPacket.checkInfoPacket(updatedRow);
                    }
                })
                .finally(() => this.searchCallListFiltered());
        }
    }

    buttonRescheduleOnClick(event: ClickEvent) {
        const selectedRows = this.tableActivities.selectedRows.map(row => row.data);
        CommonDialogs.showInputDialog("Reschedule Date Due to:", "Reschedule Activities", {
            required: true,
            displayType: DisplayType.DATE
        }, {
            panelTitleProps: { backgroundColor: "primary.darker", color: "primary.reverse" },
            okProps: { backgroundColor: "primary", color: "primary.reverse", width: 110 },
        }).then((response) => {
            if (response) {
                const progressSnackbar = Snackbar.showDownloadSnackbar("Action in Progress", "Rescheduling...", { persist: true });
                const actions = selectedRows.map(row => row.data);
                return Api.post("lme/crm/reschedule-action", { actions, date_due: response })
                .then((result: any) => {
                    if (selectedRows.length > 1 || result.data?.filter(r => !!r.error).length > 0) {
                        CRMMultiSelectErrorModal.showModal(
                            "Reschedule Activities",
                            result.data?.filter(r => !r.error).length,
                            result.data?.filter(r => !!r.error).map(r => new ModelRow(null, false, r))
                        );
                    }
                })
                .finally(() => {
                    progressSnackbar.dismiss();
                    this.searchCallListFiltered();
                })
            }
        });
    }

    tabsetCRMBeforeTabSelection(event: SelectionEvent) {
        const tab = event.oldSelection as Tab;
        if (tab) {
            tab.forEveryChildComponent((component) => {
                if (component instanceof Table) {
                    component.clearSelectedRows();
                }
         });
        }
    }
}
